import React, { useMemo } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Card } from '../ui';
import CategorySelect, { CATEGORIES } from '../ui/CategorySelect';
import { CHAT_TYPES, DISCOVERY_SOURCES } from '../../utils/groupBadges';
import FilterOptionSelect from './FilterOptionSelect';

const Section = ({ title, description, children }) => (
  <div className="space-y-4 rounded-lg border border-gray-200 p-4">
    <div>
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">{children}</div>
  </div>
);

const NumberField = ({ label, value, placeholder, onChange, min, max, step }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type="number"
      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
);

const SelectField = ({ label, value, onChange, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <select
      className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
    >
      {children}
    </select>
  </div>
);

const GroupsFilter = ({
  searchTerm,
  setSearchTerm,
  filters,
  updateFilter,
  countries = [],
  languages = [],
  regions = [],
  clearFilters,
  hasActiveFilters,
}) => {
  const optionLookup = useMemo(
    () => ({
      country: Object.fromEntries(countries.map((option) => [option.value, option.label])),
      language: Object.fromEntries(languages.map((option) => [option.value, option.label])),
      region: Object.fromEntries(regions.map((option) => [option.value, option.label])),
    }),
    [countries, languages, regions]
  );

  const activeFilterChips = useMemo(() => {
    const chips = [];

    if (searchTerm) {
      chips.push({ key: 'search', label: `Search: ${searchTerm}` });
    }

    if (filters.theme_id) {
      chips.push({
        key: 'theme_id',
        label: `Theme: ${CATEGORIES[filters.theme_id] || filters.theme_id}`,
      });
    }

    if (filters.country) {
      chips.push({
        key: 'country',
        label: `Country: ${optionLookup.country[filters.country] || filters.country}`,
      });
    }

    if (filters.language) {
      chips.push({
        key: 'language',
        label: `Language: ${optionLookup.language[filters.language] || filters.language}`,
      });
    }

    if (filters.region) {
      chips.push({
        key: 'region',
        label: `Region: ${optionLookup.region[filters.region] || filters.region}`,
      });
    }

    [
      ['subscription_status', 'Subscription'],
      ['basic_info_status', 'Basic Info'],
      ['ai_analysis_status', 'AI Analysis'],
      ['discovery_source', 'Source'],
      ['chat_type', 'Chat Type'],
      ['min_members', 'Min Members'],
      ['max_members', 'Max Members'],
      ['min_quality', 'Min Quality'],
      ['max_quality', 'Max Quality'],
      ['min_messages_count', 'Min Messages'],
      ['max_messages_count', 'Max Messages'],
      ['min_posts_per_hour', 'Min Posts/H'],
      ['max_posts_per_hour', 'Max Posts/H'],
      ['min_unique_senders', 'Min Senders'],
      ['max_unique_senders', 'Max Senders'],
    ].forEach(([key, label]) => {
      if (filters[key]) {
        chips.push({ key, label: `${label}: ${filters[key]}` });
      }
    });

    return chips;
  }, [filters, optionLookup, searchTerm]);

  return (
    <Card>
      <div className="space-y-4 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
            <p className="mt-1 text-sm text-gray-500">
              Narrow the groups base before running bulk actions or export.
            </p>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear All Filters
            </button>
          )}
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2">
            {activeFilterChips.map((chip) => (
              <span
                key={chip.key}
                className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
              >
                {chip.label}
              </span>
            ))}
          </div>
        )}

        <Section
          title="Search & Geography"
          description="Use geography filters for client-ready subsets. Language and region are raw metadata values from the database."
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search groups..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
            <CategorySelect
              value={filters.theme_id || ''}
              onChange={(value) => updateFilter('theme_id', value)}
              placeholder="All Themes"
              searchable={true}
            />
          </div>

          <FilterOptionSelect
            label="Country"
            value={filters.country}
            onChange={(value) => updateFilter('country', value)}
            options={countries}
            placeholder="All Countries"
            searchPlaceholder="Search countries..."
          />

          <FilterOptionSelect
            label="Language"
            value={filters.language}
            onChange={(value) => updateFilter('language', value)}
            options={languages}
            placeholder="All Languages"
            searchPlaceholder="Search languages..."
          />

          <FilterOptionSelect
            label="Region"
            value={filters.region}
            onChange={(value) => updateFilter('region', value)}
            options={regions}
            placeholder="All Regions"
            searchPlaceholder="Search regions..."
          />
        </Section>

        <Section
          title="Status & Quality"
          description="Operational filters for enrichment progress and prioritization."
        >
          <SelectField
            label="Subscription Status"
            value={filters.subscription_status}
            onChange={(value) => updateFilter('subscription_status', value)}
          >
            <option value="">All</option>
            <option value="unknown">Unknown</option>
            <option value="subscribed">Subscribed</option>
            <option value="unsubscribed">Unsubscribed</option>
            <option value="not_member">Not Member</option>
            <option value="invite_requested">Invite Requested</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </SelectField>

          <SelectField
            label="Basic Info Check"
            value={filters.basic_info_status}
            onChange={(value) => updateFilter('basic_info_status', value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="inaccessible">Inaccessible</option>
          </SelectField>

          <SelectField
            label="AI Analysis"
            value={filters.ai_analysis_status}
            onChange={(value) => updateFilter('ai_analysis_status', value)}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </SelectField>

          <SelectField
            label="Discovery Source"
            value={filters.discovery_source}
            onChange={(value) => updateFilter('discovery_source', value)}
          >
            <option value="">All Sources</option>
            <option value={DISCOVERY_SOURCES.MANUAL}>Manual</option>
            <option value={DISCOVERY_SOURCES.ACCOUNT_DISCOVERY}>Account Discovery</option>
          </SelectField>

          <NumberField
            label="Min Quality"
            value={filters.min_quality}
            placeholder="e.g. 70"
            min="0"
            max="100"
            onChange={(value) => updateFilter('min_quality', value)}
          />

          <NumberField
            label="Max Quality"
            value={filters.max_quality}
            placeholder="e.g. 100"
            min="0"
            max="100"
            onChange={(value) => updateFilter('max_quality', value)}
          />
        </Section>

        <Section
          title="Audience & Activity"
          description="Volume and engagement filters for the current matched subset."
        >
          <NumberField
            label="Min Members"
            value={filters.min_members}
            placeholder="e.g. 100"
            onChange={(value) => updateFilter('min_members', value)}
          />

          <NumberField
            label="Max Members"
            value={filters.max_members}
            placeholder="e.g. 10000"
            onChange={(value) => updateFilter('max_members', value)}
          />

          <NumberField
            label="Min Messages Count"
            value={filters.min_messages_count}
            placeholder="e.g. 10"
            min="0"
            onChange={(value) => updateFilter('min_messages_count', value)}
          />

          <NumberField
            label="Max Messages Count"
            value={filters.max_messages_count}
            placeholder="e.g. 100"
            min="0"
            onChange={(value) => updateFilter('max_messages_count', value)}
          />

          <SelectField
            label="Chat Type"
            value={filters.chat_type}
            onChange={(value) => updateFilter('chat_type', value)}
          >
            <option value="">All Types</option>
            <option value={CHAT_TYPES.SUPERGROUP}>Supergroup</option>
            <option value={CHAT_TYPES.CHANNEL}>Channel</option>
            <option value={CHAT_TYPES.BASIC_GROUP}>Basic Group</option>
            <option value={CHAT_TYPES.PRIVATE}>Private Chat</option>
            <option value={CHAT_TYPES.SECRET}>Secret Chat</option>
          </SelectField>

          <NumberField
            label="Min Posts/Hour"
            value={filters.min_posts_per_hour}
            placeholder="e.g. 5"
            min="0"
            step="0.1"
            onChange={(value) => updateFilter('min_posts_per_hour', value)}
          />

          <NumberField
            label="Max Posts/Hour"
            value={filters.max_posts_per_hour}
            placeholder="e.g. 50"
            min="0"
            step="0.1"
            onChange={(value) => updateFilter('max_posts_per_hour', value)}
          />

          <NumberField
            label="Min Unique Senders"
            value={filters.min_unique_senders}
            placeholder="e.g. 10"
            min="0"
            max="100"
            onChange={(value) => updateFilter('min_unique_senders', value)}
          />

          <NumberField
            label="Max Unique Senders"
            value={filters.max_unique_senders}
            placeholder="e.g. 100"
            min="0"
            max="100"
            onChange={(value) => updateFilter('max_unique_senders', value)}
          />
        </Section>
      </div>
    </Card>
  );
};

export default GroupsFilter;
